import { ReactElement, useCallback } from 'react';

import {
   PatientConsentDocumentAttachmentInitializationResult,
   PatientConsentDocumentInitializeAttachmentsCommand,
   PatientConsentDocumentRemoveAttachmentsCommand,
} from '@dreryk/egabinet-patient-frontend-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { PromiseVoidFunction, useBoolean } from '@dreryk/react-common';
import { AttachmentsQrCodeUrl, dispatchSnackbarNegativeEvent } from '@dreryk/react-components';

import { patientMedicalInformationTranslationKeys } from '../../../translations';

import { ConsentsAndAuthorizationsDocumentScannerModal } from './ConsentsAndAuthorizationsDocumentsModal';

type ConsentsAndAuthorizationsDocumentScannerModalOpenerRenderProps = { openModal: PromiseVoidFunction };
type ConsentsAndAuthorizationsDocumentScannerModalOpenerProps = {
   readonly children: (renderProps: ConsentsAndAuthorizationsDocumentScannerModalOpenerRenderProps) => ReactElement;
   readonly onConfirm: VoidFunction;
   readonly consentDocumentId: number;
   readonly consentDocumentName: string;
};

export const ConsentsAndAuthorizationsDocumentScannerModalOpener = (
   props: ConsentsAndAuthorizationsDocumentScannerModalOpenerProps
): ReactElement => {
   const { onConfirm } = props;
   const [{ setTrue: openModal, setFalse: closeModal }, modalOpened] = useBoolean(false);

   const { data: patientConsentDocument, executeAsync: initializePatientConsentDocumentAttachments } = useCqrsCommand<
      PatientConsentDocumentAttachmentInitializationResult,
      void
   >(
      () => {
         const command = new PatientConsentDocumentInitializeAttachmentsCommand();
         command.consentDocumentId = props.consentDocumentId;
         return command;
      },
      {
         onUnexpectedError: () => {
            dispatchSnackbarNegativeEvent(patientMedicalInformationTranslationKeys.qrCodeGenerateFailed);
         },
      }
   );

   const { executeAsync: removePatientConsentDocumentRemoveAttachments } = useCqrsCommand<
      PatientConsentDocumentAttachmentInitializationResult,
      void
   >(
      () => {
         const command = new PatientConsentDocumentRemoveAttachmentsCommand();
         command.consentDocumentId = props.consentDocumentId;
         return command;
      },
      {
         onUnexpectedError: () => {
            dispatchSnackbarNegativeEvent(patientMedicalInformationTranslationKeys.qrCodeGenerateFailed);
         },
      }
   );

   const handleOpenModal = useCallback(async () => {
      await initializePatientConsentDocumentAttachments();
      openModal();
   }, [initializePatientConsentDocumentAttachments, openModal]);

   const handleClose = useCallback(() => {
      removePatientConsentDocumentRemoveAttachments();
      closeModal();
   }, [closeModal, removePatientConsentDocumentRemoveAttachments]);

   const handleConfirm = useCallback(() => {
      onConfirm();
      closeModal();
   }, [onConfirm, closeModal]);

   return (
      <>
         {props.children({ openModal: handleOpenModal })}
         {modalOpened && patientConsentDocument ? (
            <ConsentsAndAuthorizationsDocumentScannerModal
               onConfirm={handleConfirm}
               onClose={handleClose}
               consentDocumentName={props.consentDocumentName}
               consentDocumentId={props.consentDocumentId}
               qrCodeUrl={patientConsentDocument.fileUploadUrl as AttachmentsQrCodeUrl}
            />
         ) : null}
      </>
   );
};
